import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dropdown, Drawer, Button, Checkbox } from 'antd';
import { ActivityEmptyState, ActivitySearchPropertyName, ActivitySkeletonContent, ExpiredSessionModal, ActivitySkeletonFilters, FilterCheckbox, FilterBudgetRange, FilterStars, FilterCategoryTree, WalletSearchSlider, WalletNextRewardCard, WhiteButton } from '@components';
import { getActivitySortOptions, isActivityFiltersChanged, isActivityFiltersEmpty, normalizeActivityBudgetRanges, GetLifeStyleStores } from '@utils';
import { CustomErrorPage, CustomSelect, BlueButton } from '@share/components';
import { activityFiltersActions, IDatesState, IActivityFiltersState, IActivityLocationsState, activityLocationActions, activityActions, getActivity, resetActivityFull, getUserWallet, loginActions } from '@share/store/slices';
import { IAccount, IBudgetRange, IUser, RewardWalletPriority } from '@share/common-types';
import { FilterSvg, ChevronSvg } from '@assets';
import { APP_SELECTOR, NULL_VALUE } from '@constants';
import { FILTERS_LABEL, Routes, R_FILTER_BUDGET, R_FILTER_NAME, R_FILTER_STARS, SortTypes, getSortByCodeByValue, DEFAULT_MEDIA_POINT, DEAL_HOME_LABEL, ALL_INCLUSIVE_KEY, LIFE_STYLE_NAME_PARAM } from '@share/constants';
import { UrlUtils, isDataValid, getAccountUsernameFromPath, GetHomeParams, VACATIONS_PAGE, navigateTo, activityDurationLabel, activityTimeOfDayLabel, activitySpecialLabel, activityRefundableLabel, RootState, AppDispatch } from '@share/utils';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { ActivityCardComponent } from '../activity-card';
import { ActivitySelectedFiltersHeader } from '../selected-filters-header';
import { ActivitySelectedFiltersFooter } from '../selected-filters-footer';

import "./style.scss"
import { IActivity, IActivityCategoryCounterItem, IActivityCounterItem, IActivityCounters } from '@common-types';
import { compareActions } from '@share/store/slices';
import { HasPermission, ViewQuotesSharePermission } from '@utils';
import { ModalCompare } from './modal-compare/component';

interface IProps {
  label?: string;
  isVacationRentals?: boolean | null;
}

const debounceTime = 500;
const scrollPixels = 450;
const maxCompareActivities = 5;

/** English and Spanish first when present; remaining facets by count descending. */
const guideLanguageFacetCompare = (a: IActivityCounterItem, b: IActivityCounterItem): number => {
  const tier = (key: string) => {
    const base = String(key).toLowerCase().split(/[-_]/)[0];
    if (base === 'en') return 0;
    if (base === 'es') return 1;
    return 2;
  };
  const pa = tier(String(a.key));
  const pb = tier(String(b.key));
  if (pa !== pb) return pa - pb;
  return (b.count || 0) - (a.count || 0);
};

/**
 * Merge primary and "similar" facet counters. Kept at module scope (it is a pure
 * function of its arguments) so its identity is stable across renders; otherwise it
 * would invalidate the `countersMerge` memo on every render and rebuild all derived
 * filter data, feeding a re-render storm (see TRAV23-4443).
 */
const mergeActivityCounters = (
  counters: IActivityCounters | null | undefined,
  similarCounters: IActivityCounters | null | undefined,
): IActivityCounters | null | undefined => {
  if (!counters && !similarCounters) {
    return NULL_VALUE;
  }
  if (!counters) {
    return similarCounters;
  }
  if (!similarCounters) {
    return counters;
  }
  const mergeCounterItems = (items1: IActivityCounterItem[], items2: IActivityCounterItem[]): IActivityCounterItem[] => {
    const mergedMap: { [key: string]: number } = {};
    items1.forEach(item => {
      const k = String(item.key);
      mergedMap[k] = (mergedMap[k] || 0) + item.count;
    });
    items2.forEach(item => {
      const k = String(item.key);
      mergedMap[k] = (mergedMap[k] || 0) + item.count;
    });
    return Object.entries(mergedMap).map(([key, count]) => ({ key, count }));
  };

  const mergeBudgetRanges = (ranges1: IBudgetRange[], ranges2: IBudgetRange[]): IBudgetRange[] => {
    const mergedMap: { [key: string]: number } = {};
    ranges1.forEach(range => {
      const key = `${range.key.from}-${range.key.to}`;
      mergedMap[key] = (mergedMap[key] || 0) + range.count;
    });
    ranges2.forEach(range => {
      const key = `${range.key.from}-${range.key.to}`;
      mergedMap[key] = (mergedMap[key] || 0) + range.count;
    });
    return Object.entries(mergedMap).map(([key, count]) => {
      const [from, to] = key.split('-');
      return {
        key: {
          from: from === 'null' ? null : Number(from),
          to: to === 'null' ? null : Number(to)
        },
        count
      };
    });
  };

  const mergeCategoryRoots = (
    a: IActivityCategoryCounterItem[] | undefined,
    b: IActivityCategoryCounterItem[] | undefined
  ): IActivityCategoryCounterItem[] => {
    if (a?.length) return a;
    if (b?.length) return b;
    return [];
  };

  return {
    amenities: mergeCounterItems(counters.amenities || [], similarCounters.amenities || []),
    accessibilities: mergeCounterItems(counters.accessibilities || [], similarCounters.accessibilities || []),
    popularLocations: mergeCounterItems(counters.popularLocations || [], similarCounters.popularLocations || []),
    budgetRanges: mergeBudgetRanges(counters.budgetRanges || [], similarCounters.budgetRanges || []),
    propertyTypes: mergeCounterItems(counters.propertyTypes || [], similarCounters.propertyTypes || []),
    boardNames: mergeCounterItems(counters.boardNames || [], similarCounters.boardNames || []),
    neighbourhoods: mergeCounterItems(counters.neighbourhoods || [], similarCounters.neighbourhoods || []),
    refundable: mergeCounterItems(counters.refundable || [], similarCounters.refundable || []),
    ratings: mergeCounterItems(counters.ratings || [], similarCounters.ratings || []),
    timeOfDays: mergeCounterItems(counters.timeOfDays || [], similarCounters.timeOfDays || []),
    specials: mergeCounterItems(counters.specials || [], similarCounters.specials || []),
    guideLanguages: mergeCounterItems(counters.guideLanguages || [], similarCounters.guideLanguages || []),
    durations: mergeCounterItems(counters.durations || [], similarCounters.durations || []),
    categories: mergeCategoryRoots(counters.categories, similarCounters.categories),
    totalFlexibleDates: (counters.totalFlexibleDates || 0) + (similarCounters.totalFlexibleDates || 0),
    total: (counters.total || 0) + (similarCounters.total || 0),
    totalFiltered: (counters.totalFiltered || 0) + (similarCounters.totalFiltered || 0),
    totalNights: (counters.totalNights || 0) + (similarCounters.totalNights || 0),
  };
};


export const ActivityResultWrapper: React.FC<IProps> = ({ isVacationRentals }) => {
  const intl = useIntl();
  const guideLanguageDisplayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([intl.locale], { type: 'language' });
    } catch {
      return null;
    }
  }, [intl.locale]);
  const history = useHistory();
  const dispatch = useDispatch<AppDispatch>();

  /* --------------------------- Redux selectors --------------------------- */
  const activityStore = useSelector((state: RootState) => state.activityStore);
  const datesStore = useSelector((state: RootState) => state.datesStore) as IDatesState;
  const activityLocationsStore = useSelector((state: RootState) => state.activityLocationsStore) as IActivityLocationsState;
  const activityFiltersStore = useSelector((state: RootState) => state.activityFiltersStore) as IActivityFiltersState;
  const loginStore = useSelector((state: RootState) => state.loginStore);
  const { activitycompare } = useSelector((state: RootState) => state.compareStore);
  const roomsStore = useSelector((state: RootState) => state.roomsStore);


  const { locationLabel } = activityLocationsStore;

  const {
    activities,
    prevActivities,
    similarActivities,
    searchedLocation,
    lastSearchedLocation,
    loadingMore,
    counters,
    filterCounters,
    similarCounters,
    isSessionExpired,
    loadingFilter,
    error,
    loading,
    selectedActivitySearchClientCash,
  } = activityStore;


  /* --------------------------- Action wrappers --------------------------- */
  const setLoadingFilter = useCallback((loadingFilter: boolean) => dispatch(activityActions.setLoadingFilter(loadingFilter)), [dispatch]);
  const setBudget = useCallback((value: string[]) => dispatch(activityFiltersActions.setBudget(value)), [dispatch]);
  const setRefundable = useCallback((value: string[]) => dispatch(activityFiltersActions.setRefundable(value)), [dispatch]);
  const setRating = useCallback((value: number[]) => dispatch(activityFiltersActions.setRating(value)), [dispatch]);
  const setTimeOfDays = useCallback((value: string[]) => dispatch(activityFiltersActions.setTimeOfDays(value)), [dispatch]);
  const setSpecials = useCallback((value: string[]) => dispatch(activityFiltersActions.setSpecials(value)), [dispatch]);
  const setGuideLanguages = useCallback((value: string[]) => dispatch(activityFiltersActions.setGuideLanguages(value)), [dispatch]);
  const setDurations = useCallback((value: string[]) => dispatch(activityFiltersActions.setDurations(value)), [dispatch]);
  const setActivityCategory = useCallback((value: string) => dispatch(activityFiltersActions.setActivityCategory(value)), [dispatch]);
  const setSortBy = useCallback((value: string) => dispatch(activityFiltersActions.setSortBy(value as SortTypes)), [dispatch]);
  const resetFilters = useCallback(() => dispatch(activityFiltersActions.resetFilters(loginStore.account?.isCapitalVacations)), [dispatch, loginStore.account]);
  const setLifeStyle = useCallback((value: string) => dispatch(loginActions.setLifeStyle(value)), [dispatch]);
  const validateLocation = useCallback((error: string) => dispatch(activityLocationActions.setError(error)), [dispatch]);
  const handleOnSelectClientCash = useCallback((selectedSearchClientCash: number | null | undefined) => {
    dispatch(activityActions.setSelectedActivitySearchClientCash({
      ...selectedActivitySearchClientCash,
      selectedSearchClientCash,
    }));
  }, [dispatch, selectedActivitySearchClientCash]);

  const {
    ratings,
    budget,
    refundable,
    sortBy,
    timeOfDays,
    specials,
    guideLanguages,
    durations,
    activityCategory,
  } = activityFiltersStore;

  const [isMobileFilter, setIsMobileFilter] = useState(false);
  const [backHomeType, setBackHomeType] = useState<string | null>(NULL_VALUE);
  const [showCompareModal, SetshowCompareModal] = useState(false);

  const isMobileFilterRef = useRef(isMobileFilter);
  const backHomeTypeRef = useRef(backHomeType);
  const filtersStoreRef = useRef(activityFiltersStore);
  const prevFiltersRef = useRef<IActivityFiltersState | null>(activityFiltersStore);
  const root = useRef<HTMLDivElement | null>(document.querySelector(APP_SELECTOR));

  useEffect(() => { isMobileFilterRef.current = isMobileFilter; }, [isMobileFilter]);
  useEffect(() => { backHomeTypeRef.current = backHomeType; }, [backHomeType]);

  useEffect(() => {
    if (!prevFiltersRef.current) prevFiltersRef.current = activityFiltersStore as IActivityFiltersState;
    filtersStoreRef.current = activityFiltersStore;
  }, [activityFiltersStore]);

  const checkIsLocationValid = (): boolean => {
    const { isLocationValid } = isDataValid(activityLocationsStore, datesStore);

    if (!isLocationValid) {
      validateLocation(intl.formatMessage({ id: 'location.error' }));
      return false;
    }

    return true;
  };

  const onScroll = useMemo(
    () =>
      debounce(() => {
        if (loadingMore || isSessionExpired) return;

        const rootEl = root.current;
        if (!rootEl) return;

        const rootHeight = rootEl.scrollHeight || rootEl.offsetHeight;
        if (window.innerHeight + window.scrollY + scrollPixels >= rootHeight) {
          dispatch(activityActions.setPageNumber());
          const total = activityStore?.counters?.totalFiltered || 0;
          const loaded = activities.length;
          const hasMore = loaded < total;
          if (activities && activities.length && hasMore && prevActivities.length) {
            dispatch(
              getActivity({
                lastSearchedLocation,
                isLoadMore: true,
                isFiltersChange: false,
                isReuseSessionKey: false,
              })
            );
          }
        }
      }, debounceTime),
    [dispatch, activities.length, counters?.totalFiltered, loadingMore, isSessionExpired]
  );

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      onScroll.cancel();
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll])


  const onClickBackButton = useCallback((e: any) => {
    e.preventDefault();
    const accountName = getAccountUsernameFromPath(history);
    const { account } = loginStore;
    const homeParams = GetHomeParams(account as IAccount);

    if (isVacationRentals) {
      navigateTo(`/${accountName}${Routes.ActivitiesSearch}/${VACATIONS_PAGE}${homeParams}`);
    } else {
      navigateTo(`/${accountName}${Routes.ActivitiesSearch}${homeParams}`);
    }
    window.location.reload();
  }, [history, loginStore, isVacationRentals]);


  useEffect(() => {
    if (activitycompare && activitycompare.length > 0) {
      const firstActivity = activitycompare[0];

      const isLocationChanged = firstActivity.locationObj?.code !== activityLocationsStore.location?.code ||
        firstActivity.locationObj?.name !== activityLocationsStore.locationLabel;
      const isDateChanged = firstActivity.startDate !== datesStore.startDate;

      const rooms1 = (firstActivity.roomOccupancies || []).map(r => ({
        adultsCount: r.adultsCount,
        adultAges: [...(r.adultAges || [])].sort(),
        kids: (r.kids || []).map(k => k.age).sort()
      }));
      const rooms2 = (roomsStore.rooms || []).map(r => ({
        adultsCount: r.adultsCount,
        adultAges: [...(r.adultAges || [])].sort(),
        kids: (r.kids || []).map(k => k.age).sort()
      }));

      const isRoomsChanged = !isEqual(rooms1, rooms2);

      if (isLocationChanged || isDateChanged || isRoomsChanged) {
        dispatch(compareActions.setclearActivityCompare());
      }
    }
  }, [dispatch, activityLocationsStore.location, activityLocationsStore.locationLabel, datesStore.startDate, roomsStore.rooms, activitycompare]);

  useEffect(() => {
    window.history.pushState(null, '', window.location.pathname + window.location.search);

    window.addEventListener('popstate', onClickBackButton);
    window.addEventListener('scroll', onScroll);

    const values = UrlUtils.getValues();
    if (values[DEAL_HOME_LABEL]) setBackHomeType(values[DEAL_HOME_LABEL] as string);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('popstate', onClickBackButton);

      const footer: HTMLDivElement | null = document.querySelector('.footer');
      if (footer) {
        document.body.style.overflow = 'auto';
        footer.style.display = 'block';
      }
    };
  }, [onClickBackButton, onScroll]);


  useEffect(() => {
    return () => {
      const footer: HTMLDivElement | null = document.querySelector('.footer');
      if (footer) {
        document.body.style.overflow = 'auto';
        footer.style.display = 'block';
      }
    };
  }, []);

  const onBudgetChange = (checkedValues: any[]) => {
    const { account } = loginStore;
    setBudget(checkedValues);
    ReactGA.event({ category: account?.name as string, action: `${R_FILTER_BUDGET}_${account?.name.toUpperCase()}`, label: `User selected budget checkbox filter on result` });
    onFiltersOrSortChange();
  };

  const onRefundableChange = (checkedValues: any[]) => { setRefundable(checkedValues); onFiltersOrSortChange(); };

  const getSortOptionsLocal = () => {
    const { account } = loginStore;
    const isB2C = account?.isB2C;
    const walletWalletSavings = account?.walletWalletSavings;
    const hasSavedProperties = activityStore.activities.some((x: any) => x.savedProperty);
    const isSaverRuleProfits = account?.isSaverRuleProfits;
    const isCustomerAgent = account?.isCustomerAgent;
    const isCapitalVacations = account?.isCapitalVacations;
    const displayRecommended = !!isSaverRuleProfits;
    const displayRecommendedCustomerAgent = !!isCustomerAgent;

    return getActivitySortOptions(isB2C as boolean, walletWalletSavings as boolean, displayRecommended, displayRecommendedCustomerAgent, hasSavedProperties, isCapitalVacations as boolean);
  };

  const onSearchSave = () => {
    const { account } = loginStore;
    ReactGA.event({ category: account?.name as string, action: `${R_FILTER_NAME}_${account?.name.toUpperCase()}`, label: `User searched by property name` });
    onFiltersOrSortChange();
  };

  const onStarsChange = (values: number[]) => {
    setRating(values);
    const { account } = loginStore;
    ReactGA.event({ category: account?.name as string, action: `${R_FILTER_STARS}_${account?.name.toUpperCase()}`, label: `Stars filter applied` });
    onFiltersOrSortChange();
  };

  const onTimeOfDaysChange = (values: string[]) => {
    setTimeOfDays(values);
    onFiltersOrSortChange();
  };

  const onSpecialsChange = (values: string[]) => {
    setSpecials(values);
    onFiltersOrSortChange();
  };

  const onGuideLanguagesChange = (values: string[]) => {
    setGuideLanguages(values);
    onFiltersOrSortChange();
  };

  const onDurationsChange = (values: string[]) => {
    setDurations(values);
    onFiltersOrSortChange();
  };

  const onActivityCategorySelect = (key: string) => {
    setActivityCategory(activityCategory === key ? '' : key);
    onFiltersOrSortChange();
  };

  const onSortChange = (value: string) => {
    setSortBy(value);
    const { account } = loginStore;
    const action = getSortByCodeByValue(value);
    if (action) {
      ReactGA.event({ category: account?.name as string, action: `${action}_${account?.name.toUpperCase()}`, label: `Sort option changed` });
    }
    if (!isMobileFilter) onFiltersOrSortChange();
  };

  const onLifeStyleChange = (value: string) => {
    setLifeStyle(value);

    UrlUtils.removeFromUrl(LIFE_STYLE_NAME_PARAM);
    UrlUtils.setUrl(LIFE_STYLE_NAME_PARAM, value);

    if (!isMobileFilter) onFiltersOrSortChange(true);
  };

  const onResetFiltersBase = () => {
    UrlUtils.setUrl(FILTERS_LABEL, null);
    resetFilters();
    onFiltersOrSortChange();
  };

  const onFiltersOrSortChangeDebounce = useCallback(debounce((isStoreChange?: boolean) => {
    const { lastSearchedLocation } = activityStore;
    const { selectedLocation } = activityLocationsStore;
    const latestFilters = filtersStoreRef.current;
    if (!lastSearchedLocation && !selectedLocation) checkIsLocationValid();
    const filtersChanged = isActivityFiltersChanged(prevFiltersRef.current as IActivityFiltersState, latestFilters);
    if (filtersChanged) {
      UrlUtils.setUrl(FILTERS_LABEL, latestFilters);
      prevFiltersRef.current = latestFilters;
      dispatch(
        getActivity({
          lastSearchedLocation,
          isLoadMore: true,
          isFiltersChange: true,
          isReuseSessionKey: false,
        })
      );
    } else if (isStoreChange) {
      const { user } = loginStore;
      dispatch(activityActions.resetActivities());
      dispatch(getUserWallet(user as IUser));
      dispatch(
        getActivity({
          lastSearchedLocation,
          isLoadMore: false,
          isFiltersChange: false,
          isReuseSessionKey: false,
        })
      );
    } else {
      setLoadingFilter(false);
    }
    prevFiltersRef.current = filtersStoreRef.current;
  }, debounceTime), [activityStore, activityLocationsStore, loginStore, activityFiltersStore, checkIsLocationValid]);

  const onFiltersOrSortChange = (isSortChange = false) => {
    setLoadingFilter(true);
    onFiltersOrSortChangeDebounce(isSortChange);
  };


  const onMobileFilterToggle = () => {
    if (!activityStore.isSessionExpired) {
      setIsMobileFilter(prev => {
        const next = !prev;
        document.body.style.overflow = next ? 'hidden' : 'auto';
        return next;
      });
    }
  };

  const onResetDrawerFilters = () => {
    onResetFiltersBase();
    setIsMobileFilter(false);

  };

  const renderBack = () => {
    const account = loginStore.account;
    const accountName = getAccountUsernameFromPath(history);
    const homeParams = GetHomeParams(account as IAccount);
    const isDisableHomes = account?.isDisableHomes;
    const isAllInclusive = backHomeType === ALL_INCLUSIVE_KEY;
    if (isDisableHomes) return null;
    return (
      <div className="back-home" onClick={() => {
        dispatch(resetActivityFull())
        if (isAllInclusive) {
          navigateTo(`/${accountName}${Routes.AllInclusive}`);
        } else {
          navigateTo(`/${accountName}${Routes.Activities}${homeParams}`);
        }
      }}>
        <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '5px' }} /> <FormattedMessage id={isAllInclusive ? 'back.to.all_inclusive' : 'back.home'} />
      </div>
    );
  };

  const { account, lifeStyle } = loginStore;
  const { location } = activityLocationsStore;

  const hasClientCash = account?.hasClientCash;
  const walletHasSliderOnSearch = account?.walletHasSliderOnSearch;
  const isCapital = account?.isCapitalVacations;
  const isCapitalAgent = account?.isCapitalVacationsAgent;

  const countersMergeSearch = useMemo(() => (filterCounters ? { ...filterCounters } : counters), [filterCounters, counters]);
  const countersMerge = useMemo(() => mergeActivityCounters(activities?.length ? countersMergeSearch : null, !!similarActivities?.length ? similarCounters : null), [countersMergeSearch, activities, similarActivities, similarCounters]);
  const resultCounters = countersMerge;
  const normalizedPriceBudgetRanges = useMemo(
    () => normalizeActivityBudgetRanges(resultCounters?.budgetRanges || []),
    [resultCounters?.budgetRanges],
  );
  const isFiltersApplied = !isActivityFiltersEmpty((activityFiltersStore || {}) as IActivityFiltersState, account?.isCapitalVacations);

  if (!isEmpty(error)) {
    return (
      <div className="activity-result-wrapper">
        <div className="activity-result-wrapper__back-link">{renderBack()}</div>
        <div className="activity-result-wrapper__wrapper">
          <CustomErrorPage message={error} />
        </div>
      </div>
    );
  }

  let isShowBudgets = false;
  let isShowRefundable = false;
  let isShowStars = true;

  if (resultCounters) {

    isShowBudgets = !!budget?.length || !!(resultCounters.budgetRanges || []).filter(({ count }: any) => !!count).length;
    isShowRefundable = !!(resultCounters.refundable || []).filter(({ count }: any) => !!count).length;
    isShowStars = !!ratings?.length || !!(resultCounters.ratings || []).filter(({ count }: any) => !!count).length || true; // Always show stars if counters exist to be consistent with top bar
  }

  const isEmptySearch = (activities && !activities.length && location?.type !== 'Hotel') || (activities && !activities.length && similarActivities && !similarActivities.length);

  const isMobile = document.body.offsetWidth <= DEFAULT_MEDIA_POINT;

  const totalFilteredHotels = (activities?.length ? (filterCounters?.totalFiltered || counters?.totalFiltered) : 0) || 0;
  const totalFilteredHotelsSimilar = (similarActivities?.length ? (similarCounters?.totalFiltered || 0) : 0) || 0;
  const totalFilteredHotelsTotal = totalFilteredHotels + totalFilteredHotelsSimilar;
  const HasViewQuotesSharePermission = HasPermission(loginStore.user as IUser, ViewQuotesSharePermission);
  const displayCompareLength = (HasViewQuotesSharePermission || isFiltersApplied) ? 0 : 1;


  const ratingCount = (star: number) =>
    resultCounters?.ratings?.find(({ key }) => Number(key) === star);
  const oneStar = ratingCount(1);
  const twoStar = ratingCount(2);
  const threeStar = ratingCount(3);
  const fourStar = ratingCount(4);
  const fiveStar = ratingCount(5);

  const plainOptionsStar = [
    { value: 5, count: fiveStar?.count || 0, disabled: fiveStar?.disabled || false },
    { value: 4, count: fourStar?.count || 0, disabled: fourStar?.disabled || false },
    { value: 3, count: threeStar?.count || 0, disabled: threeStar?.disabled || false },
    { value: 2, count: twoStar?.count || 0, disabled: twoStar?.disabled || false },
    { value: 1, count: oneStar?.count || 0, disabled: oneStar?.disabled || false },
  ];

  const handleCompare = (activity: IActivity) => {
    const activityIndex = activitycompare.findIndex(a => a.code === activity.code);
    const { startDate } = datesStore;
    const { location } = activityLocationsStore;
    const { roomsSelected } = roomsStore;
    const { sessionKey } = activityStore;
    if (activityIndex != -1) {
      const updatedActivityCompare = activitycompare.filter(a => a.code !== activity.code);
      dispatch(compareActions.setActivityToCompare(updatedActivityCompare));
    } else {
      dispatch(compareActions.setActivityToCompare([...activitycompare, { ...activity, startDate: startDate, locationObj: location, roomOccupancies: roomsSelected, sessionKey: sessionKey }]));
    }
  };

  return (
    <div className="activity-result-wrapper">
      <div className="activity-result-wrapper__back-link">{renderBack()}</div>

      <div className="activity-result-wrapper__wrapper">
        {activitycompare?.length ? (
          <>
            <div className="activity-result-wrapper__compare-message">
              <span className="compare-message-text">
                {activitycompare.length > 1 ? (<FormattedMessage id="results.activity.card.compare.message" values={{ selected: activitycompare.length, total: maxCompareActivities }} />) : null}
                {activitycompare.length === 1 ? (<FormattedMessage id="results.activity.card.compare.message.select_more" />) : null}
              </span>
              <div className="compare-buttons">
                {activitycompare.length ? (
                  <WhiteButton className="compare-clear-button" onClick={() => dispatch(compareActions.setActivityToCompare([]))}>
                    <FormattedMessage id="results.activity.card.compare.clear_selection" />
                  </WhiteButton>) : null}

                <BlueButton className="compare-button" disabled={activitycompare.length <= displayCompareLength} onClick={() => SetshowCompareModal(true)}>
                  {HasViewQuotesSharePermission ? <FormattedMessage id="results.activity.card.compare.share" /> : <FormattedMessage id="results.activity.card.compare" />}
                </BlueButton>
              </div>
            </div>

          </>
        ) : null}
        <div className={`activity-result-wrapper__content-wrapper ${isMobileFilter ? 'is-mobile' : ''} ${isEmptySearch ? 'is-empty-search' : ''}`} style={{ paddingBottom: loadingMore ? '0px' : isEmptySearch ? '80px' : '80px' }}>

          <div><h3 className='activity-result-wrapper__top-filters-title'>{locationLabel}</h3></div>

          {!loadingFilter && !loadingMore && !loading && !isEmptySearch && (isFiltersApplied) ? (
            <ActivitySelectedFiltersHeader onFiltersOrSortChange={onFiltersOrSortChange} />
          ) : null}

          {/* Session expired */}
          {isSessionExpired && <div className="activity-result-wrapper__expired-message"><ExpiredSessionModal visible={isSessionExpired} /></div>}


          {/* Empty search */}
          {isEmptySearch && !loadingMore && !loadingFilter && !loading ? (
            <>
              <div className="activity-result-wrapper__top-filters activity-result-wrapper__top-filters--disabled">
                <div className="activity-result-wrapper__horizontal-filters">
                  <div className="filter-item all-filters">
                    <FilterSvg />
                    <span>All Filters</span>
                  </div>
                  <div className="filter-search-wrapper-dropdown">
                    <div className="filter-item">
                      <span>Price</span>
                      <ChevronSvg />
                    </div>
                    <div className="filter-item">
                      <span>Refundable</span>
                      <ChevronSvg />
                    </div>
                  </div>
                  <div className="activity-result-wrapper__results-header-info">
                    <div className="activity-result-wrapper__results-count">
                      <FormattedMessage id="no.activities.found" />
                    </div>
                  </div>
                </div>
              </div>
              <ActivityEmptyState location={searchedLocation} />
            </>
          ) : (
            <>
              <div className="activity-result-wrapper__top-filters">
                {(loadingFilter || loading) ? (
                  <ActivitySkeletonFilters />
                ) : (
                  <>
                    <div className="activity-result-wrapper__horizontal-filters">
                      <div className="filter-item all-filters" onClick={onMobileFilterToggle}>
                        <FilterSvg />
                        <span>All Filters</span>
                      </div>

                      <div className='filter-search-wrapper-dropdown'>

                        <Dropdown menu={{
                          items: [{
                            key: 'price',
                            label: (
                              <div className="filter-dropdown-menu filter-dropdown-menu--budget activity-search-wrapper" onClick={(e) => e.stopPropagation()} >
                                <FilterBudgetRange
                                  layout="compact"
                                  budgetRanges={normalizedPriceBudgetRanges}
                                  values={budget}
                                  onChange={onBudgetChange}
                                  disabled={isSessionExpired || loadingFilter}
                                />
                              </div>
                            )
                          }]
                        }} trigger={['click']}>
                          <div className="filter-item">
                            <span>Price</span>
                            <ChevronSvg />
                          </div>
                        </Dropdown>

                        <Dropdown menu={{
                          items: [{
                            key: 'refundable',
                            label: (
                              <div className="filter-dropdown-menu activity-search-wrapper" onClick={(e) => e.stopPropagation()}>
                                <Checkbox.Group value={refundable} onChange={onRefundableChange}>
                                  {resultCounters?.refundable?.filter((item) => item.count > 0).map((item) => (
                                    <div className="filter-dropdown-menu__item" key={item.key}>
                                      <Checkbox value={item.key}>
                                        <span>{item.key}</span>
                                        <span className="filter-count">{item.count}</span>
                                      </Checkbox>
                                    </div>
                                  ))}
                                </Checkbox.Group>
                              </div>
                            )
                          }]
                        }} trigger={['click']}>
                          <div className="filter-item">
                            <span>Refundable</span>
                            <ChevronSvg />
                          </div>
                        </Dropdown>

                      </div>

                      <div className="activity-result-wrapper__results-header-info">
                        <div className="activity-result-wrapper__results-count">
                          <FormattedMessage id="activity.activities.found" values={{ count: totalFilteredHotelsTotal }} />
                        </div>
                        <div className="activity-result-wrapper__sort-section">
                          {isCapital && !isCapitalAgent ? (
                            <div className="activity-result-wrapper__sort-select capital-vacations">
                              <CustomSelect
                                disabled={isSessionExpired || loadingFilter}
                                value={lifeStyle || ''}
                                onChange={onLifeStyleChange as any}
                                options={GetLifeStyleStores()}
                              />
                            </div>) : null}

                          <CustomSelect
                            disabled={isSessionExpired || loadingFilter}
                            value={sortBy}
                            onChange={onSortChange as any}
                            options={getSortOptionsLocal()}
                          />
                        </div>
                      </div>
                    </div>
                  </>)}

              </div>

              {hasClientCash && walletHasSliderOnSearch ? (
                <div
                  className="activity-result-wrapper__top-section"
                  style={{
                    backgroundColor: '#FFFFFF',
                    marginBottom: '20px',
                    borderRadius: '8px',
                    marginTop: isCapital ? '8px' : '0px',
                    minHeight: 'unset',
                    border: isCapital ? '3px solid #006098' : '1px solid #D2D2D2',
                  }}
                >
                  <WalletSearchSlider
                    display={true}
                    selectedClientCash={selectedActivitySearchClientCash?.selectedSearchClientCash as number}
                    onSelectClientCash={handleOnSelectClientCash}
                  />
                </div>
              ) : null}

              {!loadingFilter &&
                !isEmptySearch &&
                loginStore.account?.rewardWalletPriority !== RewardWalletPriority.Best ? (
                <div className="activity-result-wrapper__next-reward">
                  <WalletNextRewardCard />
                </div>
              ) : null}

              <div>
                {(loading || loadingFilter) && (
                  <ActivitySkeletonContent label="" isTitle={false} />
                )}

                {(!loading && !loadingFilter) && (
                  <div className="activity-result-wrapper__activities-grid">
                    {activities.map((activity: any, index: number) => {
                      const isInCompareList = activitycompare?.map((a) => a.code)?.includes(activity.code);
                      return (
                        <div key={index} className="activity-result-wrapper__activities-grid__item">
                          <ActivityCardComponent
                            disabled={isSessionExpired || loadingFilter}
                            activity={activity}
                            disableCompare={
                              !(activities?.length > displayCompareLength && (isInCompareList || activitycompare?.length < maxCompareActivities))
                            }
                            compared={isInCompareList}
                            onCompareSelect={handleCompare}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>


              {loadingMore && !loadingFilter && <ActivitySkeletonContent label="" isTitle={false} />}
            </>
          )}
          {!loadingFilter && !loadingMore && !loading && <ActivitySelectedFiltersFooter isVacationRentals={isVacationRentals} onFiltersOrSortChange={onFiltersOrSortChange} />}
        </div>
      </div>
      <ModalCompare
        visible={showCompareModal}
        activities={activitycompare}
        onCancel={() => SetshowCompareModal(false)}
        onRemove={handleCompare}
      />
      <Drawer
        placement="right"
        onClose={() => onMobileFilterToggle()}
        visible={isMobileFilter}
        destroyOnClose
        width={isMobile ? '100%' : 400}
        extra={
          <Button type="link"
            className={`${!isFiltersApplied ? 'filter_clear_button_disabled' : 'filter_clear_button'}`}
            disabled={!isFiltersApplied}
            onClick={onResetDrawerFilters}>
            <FormattedMessage id="clearAll" />
          </Button>
        }
        className="activity-result-wrapper__filters-drawer"
      >
        <div className={`filters-drawer-content ${!loadingFilter && resultCounters ? '' : 'skelton-loading'}`}>
          <>
            {isShowRefundable && (
              <FilterCheckbox
                title="refundable"
                options={resultCounters?.refundable?.filter(item => item.count > 0).map(item => ({
                  value: String(item.key),
                  label: activityRefundableLabel(intl, String(item.key)),
                  count: item.count
                }))}
                values={refundable}
                onChange={onRefundableChange}
              />
            )}

            <ActivitySearchPropertyName disabled={isSessionExpired || loadingFilter} onSaveValue={onSearchSave} title="activity.search.activity.name" />

            {isShowBudgets && (
              <div className="filters-drawer-budget">
                <p className="filter__title secondary-font">
                  <FormattedMessage id="budget" />
                </p>
                <FilterBudgetRange
                  layout="comfortable"
                  budgetRanges={normalizedPriceBudgetRanges}
                  values={budget}
                  onChange={onBudgetChange}
                  disabled={isSessionExpired || loadingFilter}
                />
              </div>
            )}

            {isShowStars && (
              <FilterStars
                title="activity.rating"
                options={plainOptionsStar}
                value={ratings}
                onChange={onStarsChange}
              />
            )}

            {(resultCounters?.timeOfDays || []).some((i: IActivityCounterItem) => (i.count || 0) > 0) && (
              <FilterCheckbox
                title="activity.filter.timeOfDay"
                options={(resultCounters?.timeOfDays || [])
                  .filter((item: IActivityCounterItem) => (item.count || 0) > 0)
                  .map((item: IActivityCounterItem) => ({
                    value: String(item.key),
                    label: activityTimeOfDayLabel(intl, String(item.key)),
                    count: item.count,
                    disabled: item.disabled,
                  }))}
                values={timeOfDays}
                onChange={onTimeOfDaysChange}
                withShowMore
                collapseAfter={6}
                disabled={isSessionExpired || loadingFilter}
              />
            )}

            {(resultCounters?.specials || []).some((i: IActivityCounterItem) => (i.count || 0) > 0) && (
              <FilterCheckbox
                title="activity.filter.specials"
                options={(resultCounters?.specials || [])
                  .filter((item: IActivityCounterItem) => (item.count || 0) > 0)
                  .map((item: IActivityCounterItem) => ({
                    value: String(item.key),
                    label: activitySpecialLabel(intl, String(item.key)),
                    count: item.count,
                    disabled: item.disabled,
                  }))}
                values={specials}
                onChange={onSpecialsChange}
                withShowMore
                collapseAfter={6}
                disabled={isSessionExpired || loadingFilter}
              />
            )}

            {(resultCounters?.guideLanguages || []).some((i: IActivityCounterItem) => (i.count || 0) > 0) && (
              <FilterCheckbox
                title="activity.filter.guideLanguage"
                options={(resultCounters?.guideLanguages || [])
                  .filter((item: IActivityCounterItem) => (item.count || 0) > 0)
                  .sort(guideLanguageFacetCompare)
                  .map((item: IActivityCounterItem) => {
                    const code = String(item.key);
                    const localized = guideLanguageDisplayNames?.of(code);
                    return {
                      value: code,
                      label: localized ?? code.toUpperCase(),
                      count: item.count,
                      disabled: item.disabled,
                    };
                  })}
                values={guideLanguages}
                onChange={onGuideLanguagesChange}
                withShowMore
                collapseAfter={6}
                disabled={isSessionExpired || loadingFilter}
              />
            )}

            {(resultCounters?.durations || []).some((i: IActivityCounterItem) => (i.count || 0) > 0) && (
              <FilterCheckbox
                title="activity.filter.duration"
                options={(resultCounters?.durations || [])
                  .filter((item: IActivityCounterItem) => (item.count || 0) > 0)
                  .map((item: IActivityCounterItem) => ({
                    value: String(item.key),
                    label: activityDurationLabel(intl, String(item.key)),
                    count: item.count,
                    disabled: item.disabled,
                  }))}
                values={durations}
                onChange={onDurationsChange}
                withShowMore
                collapseAfter={6}
                disabled={isSessionExpired || loadingFilter}
              />
            )}

            {(resultCounters?.categories || []).length > 0 && (
              <FilterCategoryTree
                titleId="activity.filter.categories"
                nodes={resultCounters?.categories as IActivityCategoryCounterItem[] | undefined}
                selectedKey={activityCategory}
                onSelect={onActivityCategorySelect}
                disabled={isSessionExpired || loadingFilter}
              />
            )}

            <div className="filters-drawer-content__footer">
              <Button type="primary" className='filter_apply_button' onClick={() => setIsMobileFilter(false)}>
                <FormattedMessage id="see.results" values={{ count: totalFilteredHotelsTotal }} />
              </Button>

            </div>
          </>
        </div>
      </Drawer>
    </div>
  );
};
